import React from "react";
import { Button as AntdButton } from "antd";
import cx from "classnames";
import Loading from "../Loading";
import { ButtonProps } from "./types";

const sizeClasses = {
  xs: "px-2 py-1 text-xs h-6",
  sm: "px-3 py-1.5 text-sm h-8",
  md: "px-4 py-2 text-base h-10",
  lg: "px-5 py-2.5 text-lg h-12",
  xl: "px-6 py-3 text-xl h-14",
};

const variantClasses = {
  primary:
    "bg-primary hover:bg-primary-dark text-white border-primary hover:border-primary-dark",
  secondary:
    "bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 hover:border-gray-400",
  success:
    "bg-done hover:bg-green-600 text-white border-done hover:border-green-600",
  danger:
    "bg-error hover:bg-red-600 text-white border-error hover:border-red-600",
  warning:
    "bg-processing hover:bg-yellow-500 text-white border-processing hover:border-yellow-500",
  ghost:
    "bg-transparent hover:bg-gray-100 text-gray-700 border-transparent hover:border-gray-200",
  outline:
    "bg-transparent hover:bg-primary-10 text-primary border border-primary hover:border-primary-dark",
  gray: "bg-gray-100 hover:bg-gray-200 text-gray-700",
};

const disabledClasses = "opacity-50 cursor-not-allowed hover:bg-current";

const Button: React.FC<ButtonProps> = ({
  children,
  label,
  variant = "primary",
  size = "md",
  fullWidth = false,
  rounded = true,
  icon,
  iconPosition = "left",
  loading = false,
  loadingText,
  disabled = false,
  onClick,
  className,
  ariaLabel,
  ...restProps
}) => {
  const buttonContent =
    loading && loadingText ? loadingText : children || label;

  const iconElement = loading ? <Loading size="small" inline /> : icon;

  const contentWithIcon = (
    <>
      {iconElement && iconPosition === "left" && (
        <span className="mr-2">{iconElement}</span>
      )}
      {buttonContent}
      {iconElement && iconPosition === "right" && (
        <span className="ml-2">{iconElement}</span>
      )}
    </>
  );

  return (
    <button
      className={cx(
        "inline-flex items-center justify-center font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary",
        sizeClasses[size],
        variantClasses[variant],
        {
          "w-full": fullWidth,
          "rounded-lg": rounded,
          "rounded-none": !rounded,
          [disabledClasses]: disabled || loading,
        },
        className
      )}
      disabled={disabled || loading}
      onClick={onClick}
      aria-label={
        ariaLabel ||
        (typeof buttonContent === "string" ? buttonContent : undefined)
      }
      aria-busy={loading}
      {...restProps}
    >
      {contentWithIcon}
    </button>
  );
};

// For backward compatibility with existing code
const LegacyButton: React.FC<any> = ({
  children,
  type = "primary",
  size = "middle",
  onClick,
  disabled,
  label = "",
  light = false,
  loading = false,
  loadingText,
  className,
  ...props
}) => {
  // Map old props to new variant system
  const mapTypeToVariant = () => {
    if (light) return "outline";
    if (props.variant === "error") return "danger";
    switch (type) {
      case "primary":
        return "primary";
      case "default":
        return "secondary";
      case "dashed":
      case "link":
      case "text":
        return "ghost";
      default:
        return "primary";
    }
  };

  const mapSize = () => {
    switch (size) {
      case "small":
        return "sm";
      case "middle":
        return "md";
      case "large":
        return "lg";
      default:
        return "md";
    }
  };

  return (
    <Button
      variant={mapTypeToVariant()}
      size={mapSize()}
      onClick={onClick}
      disabled={disabled}
      loading={loading}
      loadingText={loadingText}
      className={className}
      {...props}
    >
      {children || label}
    </Button>
  );
};

// Export both for gradual migration
export { LegacyButton };
export default Button;
